import _ from "lodash"
import React, {  } from "react"

import { Invention } from "../../inventions/InventionsProvider"
import { family_member, patent_family } from "../../data"
import { useRoles } from "../../user/Auth"
import { calculateClaimScopesByMemberId, useProductMapping } from "../../products/products"
import { Family, Member } from "../patents"
import { Commodity } from '../../products/products'
import { useInventions } from "../../inventions/InventionsProvider"
import { useFilteredPatents } from "../../filter/FilteredPatents"
import { useFilteredCommodities } from "../../filter/FilteredCommodities"
import { usePatents } from "../PatentsProvider"
import { useClaims } from "../../claims/ClaimsProvider"
import { useProducts } from "../../products/ProductsProvider"
import { useDwSettingsProvider } from "./DwSettings"

export const byFamilyMember = "by-family-member"
export const byPatentFamily = "by-patent-family"
export const byClaimScopes = "by-claim-scopes"
export const byProduct = "by-product"
export const byInvention = "by-invention"


export interface Row {
    families: Family[];
    members: Member[];
    products: Commodity[];
    claimScopeIds: number[];
    inventions: Invention[];
    key: string | number;
}

function useRows(groupBy: string, doOuterJoin: boolean): Row[] {
    const { hasClaimScopes, hasJoin } = useRoles()
    const { memberById, families: allFamilies, members: allMembers } = usePatents()
    const {
        commodities: allProducts,
        commoditiesByFamilyId: allCommodititesByFamilyId,
        commoditiesByClaimScopeId,
    } = useProducts()
    const { claims } = useClaims()
    const { families, familyById, membersByFamilyId, members } = useFilteredPatents()
    const { commodities, isLookingForEmpty } = useFilteredCommodities()
    const { claimScopeCommoditiesByPatentFamilyId, commoditiesByFamilyId, membersByCommodityId, commoditiesByMemberId, claimScopeCommoditiesByFamilyMemberId, claimScopeMembersByCommodityId } = useProductMapping()
    const { inventions, inventionsByMemberId } = useInventions()

    const allCommodititesByClaimScopeId = hasClaimScopes ? commoditiesByClaimScopeId : {}

    function hasAnyProtection(families: Family[], claimScopeIds: number[]) {
        return families.some(f => f.patentFamilyId in allCommodititesByFamilyId) ||
            claimScopeIds.some(id => id in allCommodititesByClaimScopeId)
    }

    let filterRowsForProducts = (row: Row) => true
    let filterRowsForPatents = (row: Row) => true

    // console.log({doOuterJoin})
    const _doOuterJoin = !hasJoin || doOuterJoin
    if (!_doOuterJoin) {
        // const isFilteringPatents = allFamilies.length !== families.length
        const isFilteringPatents = allMembers.length !== members.length
        const isFilteringCommodities = allProducts.length !== commodities.length

        filterRowsForProducts = (row: {products: Commodity[], families: Family[], claimScopeIds: number[]}) => {
            return !isFilteringCommodities || row.products.length > 0 || (isLookingForEmpty && !hasAnyProtection(row.families, row.claimScopeIds))
        }

        filterRowsForPatents = (row: {families: Family[], members: Member[]}) => {
            // const r =  !isFilteringPatents || row.families.length > 0
            // console.log({isFilteringPatents, r})
            return !isFilteringPatents || row.families.length > 0
        }
    }

    function inventionsByMembers(members: {familyMemberId?: number}[]) {
        return _(members)
            .flatMap(m => inventionsByMemberId[m.familyMemberId] ?? [])
            .unionBy(i => i.inventionId)
            .value()
    }

    // TODO: sort all families/members by internal reference
    const claimScopesByMemberId = hasClaimScopes
        ? calculateClaimScopesByMemberId(claims)
        : {}

    switch(groupBy) {
        case byFamilyMember:
        case family_member:
            return _(membersByFamilyId)
                .toPairs()
                .filter(([familyId]) => familyById[familyId] !== undefined)
                .sortBy(([familyId]) => familyById[familyId].internalReference) // TODO: needed?
                .flatMap(([familyId, members]) => {
                    return members.map(m => ({
                        families: [familyById[familyId]],
                        members: [m],
                        products: ((hasClaimScopes ? claimScopeCommoditiesByFamilyMemberId : {})[m.familyMemberId] 
                            ?? commoditiesByMemberId[m.familyMemberId]) ?? [],
                        claimScopeIds: claimScopesByMemberId[m.familyMemberId] ?? [],
                        inventions: inventionsByMemberId[m.familyMemberId] ?? [],
                        key: m.internalReference,
                    }))
                })
                .filter(filterRowsForProducts)
                .value()
        case byPatentFamily:
        case patent_family:
            return _(families)
                .sortBy(f => f.internalReference)
                .map(f => {
                    const members = membersByFamilyId[f.patentFamilyId] ?? []
                    return {
                        families: [f],
                        members,
                        // This can have undefined. Why?
                        products: ((hasClaimScopes ? claimScopeCommoditiesByPatentFamilyId : {})[f.patentFamilyId] 
                            ?? commoditiesByFamilyId[f.patentFamilyId]) ?? [],
                        claimScopeIds: _(members).flatMap(m => claimScopesByMemberId[m.familyMemberId] ?? []).uniq().value(),
                        inventions: inventionsByMembers(members),
                        key: f.internalReference,
                    }
                })
                .filter(filterRowsForProducts)
                .value()
        
        case byClaimScopes:
        case 'claimScope':
            return _(membersByFamilyId).toPairs().flatMap(([familyId, members]) => {
                const families = [familyById[familyId]]
                return _(members)
                    .map(({familyMemberId}) => ({familyMemberId, claimScopes: claimScopesByMemberId[familyMemberId] ?? []}))
                    .groupBy(({claimScopes}) => claimScopes.join('-'))
                    .toPairs()
                    .map(([key, value]) => ({
                        families,
                        members: value.map(({familyMemberId}) => memberById[familyMemberId]) ?? [],
                        products: claimScopeCommoditiesByFamilyMemberId[value[0]?.familyMemberId] ?? [],
                        claimScopeIds: _(value).flatMap(({claimScopes}) => claimScopes).uniq().value(),
                        inventions: inventionsByMembers(value),
                        key: `${familyId}-${key}`,
                    }))
                    .filter(filterRowsForProducts)
                    .value()
            }).value()
        case byProduct:
            return _(commodities)
                .map(c => {
                    const _members = ((hasClaimScopes ? claimScopeMembersByCommodityId : {})[c.commodityId] 
                        ?? membersByCommodityId[c.commodityId]) ?? []
                    const members = _(_members).filter(m => m.patentFamilyId in familyById).value()
                    const families = _(members).uniqBy(m => m.patentFamilyId).map(m => familyById[m.patentFamilyId]).value()
                    //console.log({c, families})
                    return {
                        families,
                        members,
                        products: [c],
                        claimScopeIds: _(members).flatMap(m => claimScopesByMemberId[m.familyMemberId]).uniq().value(),
                        inventions: inventionsByMembers(members),
                        key: c.commodityId,
                    }
                })
                .filter(filterRowsForPatents)
                .value()
        case byInvention: 
            return _(inventions)
                .map(invention => {
                    const member = memberById[invention.familyMemberId]
                    const members = member ? [member] : []
                    const families = _(members).map(m => familyById[m.patentFamilyId]).filter(Boolean).value()
                    return {
                        families,
                        members,
                        inventions: [invention],
                        products: ((hasClaimScopes ? claimScopeCommoditiesByFamilyMemberId : {})[member?.familyMemberId] 
                        ?? commoditiesByMemberId[member?.familyMemberId]) ?? [],
                        claimScopeIds: _(members).flatMap(m => claimScopesByMemberId[m.familyMemberId]).uniq().value(),
                        key: invention.inventionId,
                    }
                })
                .filter(filterRowsForPatents)
                .filter(filterRowsForProducts)
                .value()
        default:
            console.warn(`Not handling ${groupBy}`)
            return []
    }
}


const RowsContext = React.createContext({
    rows: [] as Row[],
})

export function useRowsProvider() {
    return React.useContext(RowsContext)
}

export function RowsProvider({children}: {children: React.ReactNode}) {
    //console.log('recalculating RP')

    const {groupBy, doOuterJoin} = useDwSettingsProvider()

    const rows = useRows(groupBy, doOuterJoin)

    const value = {
        rows,
    }

    return (
        <RowsContext.Provider {...{value}}>
            {children}
        </RowsContext.Provider>
    )
}

export function useGroupableFields() {
    const {hasClaimScopes} = useRoles()

    const groupableFields = [ 
        byFamilyMember, 
        byPatentFamily,  
        hasClaimScopes && byClaimScopes, 
        byProduct, 
        byInvention,
    ].filter(Boolean) as string[]

    return groupableFields
}

